import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api";
import { ApiError, type GovernanceDoc } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../compose/controls";
import { BookOpen, Clock, FileText, ShieldCheck } from "../compose/Icon";
import { EmptyState, ErrorState, SkeletonRows, Spinner } from "../compose/states";
import { useToast } from "../compose/Toast";
import { formatDate } from "../lib/format";
import { useAsync } from "../lib/useAsync";
import "../styles/governance.css";

/**
 * Governance `/w/:slug/governance` (brief §governance, component-map). The "how
 * content is produced / what good looks like" editing surface: the workspace's
 * agent definitions + content guidelines, each openable in an editable markdown
 * form with Save → new version, plus the version history. VIEW is any member;
 * EDIT is staff — the server enforces both (a non-staff Save → 403). A non-member
 * never reaches here (the shell redirects), and the API 404s them regardless.
 */
export function Governance() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user, memberships } = useAuth();
  const docs = useAsync(() => api.listGovernance(slug), [slug]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const canEdit =
    !!user?.is_staff ||
    memberships.some((m) => m.workspace_slug === slug && m.role === "staff");

  const agents = useMemo(
    () => (docs.data ?? []).filter((d) => d.category === "agent"),
    [docs.data],
  );
  const guides = useMemo(
    () => (docs.data ?? []).filter((d) => d.category === "guide"),
    [docs.data],
  );

  // Default-select the first artifact once the list resolves.
  useEffect(() => {
    if (selectedId == null && docs.data && docs.data.length > 0) {
      setSelectedId(docs.data[0].id);
    }
  }, [docs.data, selectedId]);

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Governance</h1>
          <p className="page__sub">
            The agent definitions and content guidelines behind this workspace —
            how content is produced and what good looks like.
          </p>
        </div>
      </div>

      {docs.loading ? (
        <div className="gov">
          <div className="gov__rail">
            <SkeletonRows rows={4} />
          </div>
          <SkeletonRows rows={6} />
        </div>
      ) : docs.error ? (
        <ErrorState body="We couldn't load the governance documents for this workspace." />
      ) : docs.data && docs.data.length > 0 ? (
        <div className="gov" data-testid="gov">
          <div className="gov__rail" data-testid="gov-list">
            <GovGroup
              heading="Agent definitions"
              icon={<FileText size={13} strokeWidth={1.5} />}
              items={agents}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
            <GovGroup
              heading="Content guidelines"
              icon={<BookOpen size={13} strokeWidth={1.5} />}
              items={guides}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </div>

          {selectedId != null ? (
            <GovEditor
              key={selectedId}
              docId={selectedId}
              canEdit={canEdit}
              onSaved={() => docs.reload()}
            />
          ) : (
            <EmptyState
              icon={<FileText size={30} strokeWidth={1.5} />}
              title="Select a document"
              body="Pick an agent definition or guideline to view and edit it."
            />
          )}
        </div>
      ) : (
        <EmptyState
          icon={<ShieldCheck size={30} strokeWidth={1.5} />}
          title="No governance documents"
          body="This workspace has no agent definitions or guidelines yet."
        />
      )}
    </div>
  );
}

function GovGroup({
  heading,
  icon,
  items,
  selectedId,
  onSelect,
}: {
  heading: string;
  icon: React.ReactNode;
  items: GovernanceDoc[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <h2 className="gov__group-h">{heading}</h2>
      <div className="gov__list">
        {items.map((d) => (
          <button
            type="button"
            key={d.id}
            className={`gov__item${d.id === selectedId ? " gov__item--active" : ""}`}
            data-testid={`gov-item-${d.id}`}
            aria-current={d.id === selectedId ? "true" : undefined}
            onClick={() => onSelect(d.id)}
          >
            <span className="gov__item-title">
              {icon} {d.title}
            </span>
            <span className="gov__item-meta">
              {d.version_count > 0
                ? `${d.version_count} version${d.version_count === 1 ? "" : "s"} · updated ${formatDate(d.updated_at)}`
                : "seeded · unedited"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function GovEditor({
  docId,
  canEdit,
  onSaved,
}: {
  docId: number;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const detail = useAsync(() => api.getGovernanceDoc(docId), [docId]);
  const toast = useToast();
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync the editor buffer whenever the persisted body changes (initial load and
  // after a successful save reloads the detail).
  const loadedBody = detail.data?.body ?? "";
  useEffect(() => {
    setBody(loadedBody);
  }, [loadedBody]);

  if (detail.loading) return <Spinner label="Loading document…" />;
  if (detail.error || !detail.data)
    return <ErrorState body="We couldn't load this document." />;

  const dirty = body !== loadedBody;
  const trimmedEmpty = body.trim().length === 0;

  const onSave = async () => {
    if (!dirty || trimmedEmpty) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.saveGovernanceDoc(docId, body);
      toast.show("New version saved");
      detail.reload();
      onSaved();
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 403
          ? "Only workspace staff can edit governance documents."
          : "We couldn't save this version. Try again.";
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const d = detail.data;
  return (
    <div className="gov__editor" data-testid="gov-editor">
      <div className="gov__editor-head">
        <div>
          <h2 className="gov__editor-title">{d.title}</h2>
          <div className="gov__editor-sub">
            <span
              className={`badge ${d.category === "agent" ? "badge--agent" : "badge--neutral"}`}
            >
              {d.category === "agent" ? "Agent definition" : "Guideline"}
            </span>{" "}
            {d.source_ref}
          </div>
        </div>
        {canEdit ? (
          <div className="gov__editor-actions">
            {dirty ? (
              <span className="gov__dirty" data-testid="gov-dirty">
                Unsaved changes
              </span>
            ) : null}
            <Button
              variant="primary"
              data-testid="gov-save"
              disabled={!dirty || trimmedEmpty || saving}
              onClick={onSave}
            >
              {saving ? "Saving…" : "Save version"}
            </Button>
          </div>
        ) : (
          <span className="gov__readonly-note" data-testid="gov-readonly-note">
            <ShieldCheck size={13} strokeWidth={1.5} /> View only — staff can edit
          </span>
        )}
      </div>

      <textarea
        className="gov__textarea"
        data-testid="gov-body"
        aria-label={`Markdown body of ${d.title}`}
        value={body}
        readOnly={!canEdit}
        spellCheck={false}
        onChange={(e) => setBody(e.target.value)}
      />

      {saveError ? (
        <span className="field__error" role="alert" data-testid="gov-save-error">
          {saveError}
        </span>
      ) : null}

      <div className="gov__history">
        <h3 className="gov__history-h">
          <Clock size={12} strokeWidth={1.5} /> Version history
        </h3>
        {d.versions.length === 0 ? (
          <span className="gov__version-meta">
            No saved versions yet — this is the seeded original.
          </span>
        ) : (
          d.versions.map((v) => (
            <div
              className="gov__version"
              key={v.id}
              data-testid={`gov-version-${v.id}`}
            >
              <span className="gov__version-meta">
                <span className="gov__version-who">{v.edited_by ?? "Unknown"}</span>{" "}
                · {formatDate(v.created_at)}
              </span>
              {canEdit ? (
                <Button
                  variant="ghost"
                  className="btn--sm"
                  data-testid={`gov-version-load-${v.id}`}
                  onClick={() => setBody(v.body)}
                >
                  Load into editor
                </Button>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
